/**
 * Auth API — Supabase Auth (email/password).
 */

import { validateNewPassword } from "../utils/passwordPolicy";
import supabase from "../config/supabase";
import { fetchMemberProfile } from "./users";
import { invokeFunction } from "./functionErrors";

const authApi = {
  // Accounts are created by the `signup` edge function, not here. Everything
  // that decides whether an account may exist — the invite code, the Turnstile
  // challenge, the rate limit — has to run somewhere the caller cannot skip, and
  // this file runs in the caller's browser. See supabase/functions/signup.
  async register({
    email,
    password,
    first_name,
    last_name,
    phone,
    location,
    location_verified = false,
    invite_code,
    turnstile_token,
  }) {
    const result = await invokeFunction(
      supabase,
      "signup",
      {
        body: {
          action: "register",
          email,
          password,
          first_name,
          last_name,
          phone,
          location,
          location_verified,
          invite_code,
          turnstile_token,
        },
      },
      "Could not create your account. Please try again.",
    );

    if (!result.success) return result;

    // The function runs as the service role, so it has no member session to hand
    // back. Sign in with the credentials we were just given to get one.
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return {
        success: false,
        error: "Your account was created. Please sign in to continue.",
      };
    }

    return { success: true, data: { user: data.user, session: data.session } };
  },

  async login({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message };
    return {
      success: true,
      data: {
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
        user: data.user,
      },
    };
  },

  async getMe() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user) return { success: false, error: error?.message || "Not authenticated" };

    return fetchMemberProfile(user.id);
  },

  async getSession() {
    const { data, error } = await supabase.auth.getSession();
    if (error) return { success: false, error: error.message };
    return { success: true, data: data.session || null };
  },

  async recordReferral(referrerId) {
    const uuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!referrerId || !uuidPattern.test(referrerId)) return { success: true };
    const { error } = await supabase.rpc("record_referral_signup", {
      p_referrer_id: referrerId,
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  // Both of these confirm the *signed-in* member's own address. The server
  // reads the address off the session token, so passing one is neither needed
  // nor honoured — that is what stops the endpoints being used to probe whether
  // an address belongs to a member, or to mail a code to someone else.
  async verify(code) {
    return invokeFunction(
      supabase,
      "verify-email-code",
      { body: { code } },
      "Could not check that code. Please try again.",
    );
  },

  async sendVerificationCode() {
    return invokeFunction(
      supabase,
      "send-verification-code",
      { body: {} },
      "Could not send a code. Please try again.",
    );
  },

  async logout() {
    await supabase.auth.signOut();
    return { success: true };
  },

  // Sends a password-reset email. The link returns the user to /reset-password,
  // where Supabase fires a PASSWORD_RECOVERY event and we let them set a new one.
  async requestPasswordReset(email) {
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  async changePassword(currentPassword, newPassword) {
    const policyError = validateNewPassword(newPassword);
    if (policyError) return { success: false, error: policyError };
    if (currentPassword === newPassword) {
      return { success: false, error: "New password must be different from current password" };
    }
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user?.email) return { success: false, error: "Please sign in again before changing your password." };
    const { error } = await supabase.auth.signInWithPassword({ email: user.email, password: currentPassword });
    if (error) return { success: false, error: "Current password could not be verified. Please try again." };
    return authApi.updatePassword(newPassword);
  },

  // Sets a new password for the user in the active (recovery) session.
  async updatePassword(newPassword) {
    const policyError = validateNewPassword(newPassword);
    if (policyError) return { success: false, error: policyError };
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};

export default authApi;
